/* ==========================================================================
   overlay-nav.js — build item 3a (BUILD-PLAN §1.2)
   --------------------------------------------------------------------------
   Behaviour for the fluted-glass overlay navigation. Owned by
   sections/header.liquid + snippets/header-overlay-nav.liquid, and loaded
   deferred by that section ONLY when `header_layout == 'overlay'`.

   WHAT IS DELIBERATELY NOT HERE
   · No scroll lock of its own. `KitInteractions.lockScroll(owner)` /
     `unlockScroll(owner)` (P12) is ref-counted BY OWNER TOKEN, and this instance
     passes itself as the token. A private boolean here would fight the cart
     drawer: whichever closed last would unlock the page while the other was
     still open. Dawn's own two lockers use body classes and are independent, so
     the page stays locked while either system says so.
   · No focus trap of its own. `trapFocus` / `removeTrapFocus` (global.js:27,
     :135) already handle the focusin/focusout/keydown triple and the initial
     focus target.
   · No reveal / stagger logic. The link stagger is pure CSS driven by `.is-open`
     plus a per-row `--overlay-nav-index`, so it also works on the first frame
     after a Shopify section re-render, before any script has run.
   · No label rewriting. Both toggle labels are in the DOM and CSS picks one from
     `aria-expanded`, so there is no string in this file to translate.
   · No IntersectionObserver and no scroll listener. This component has no
     scroll-linked behaviour at all.

   THE ONE THING WORTH READING TWICE
   The panel is hidden by `visibility: hidden`, not `display: none`, so its links
   leave the tab order and the accessibility tree while it is closed WITHOUT
   losing the fade. That means this file must never toggle `hidden` or `display`:
   doing so would break the transition and duplicate a state that already exists.
   ========================================================================== */

(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';

  /* Mirrored onto the HOST, not just the panel, because the fixed chrome is a
     SIBLING of the panel — it is drawn above it so the toggle can double as the
     close control — and a sibling cannot be selected from the panel's state.
     The chrome needs to know the panel is open so it can stop difference-blending
     against a ground that is now flat and dark (see component-overlay-nav.css,
     "THE CHROME STOPS BLENDING"). */
  var HOST_OPEN_CLASS = 'overlay-nav--open';

  /* And onto <html>, because some of what the open panel has to sit above is
     not in this component's subtree at all. A section elsewhere on the page
     cannot see the header's state any other way — it is neither an ancestor nor
     a descendant — and :has() would put a live subtree query on the document
     root for something a class already answers. Anything that needs to yield to
     the open menu keys off html.overlay-nav-is-open. */
  var ROOT_OPEN_CLASS = 'overlay-nav-is-open';

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function readBool(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (raw === null) return fallback;
    return raw !== 'false';
  }

  if (!window.customElements || typeof window.HTMLElement === 'undefined') return;

  var OverlayNav = class extends HTMLElement {
    connectedCallback() {
      /* A custom element that is moved in the DOM (the theme editor re-parents a
         selected section) disconnects and reconnects. Re-binding would stack a
         second set of document listeners, so binding happens exactly once and
         teardown is what un-does it. */
      if (this.bound) return;

      this.panel = this.querySelector('[data-overlay-nav-panel]');
      this.toggleButton = this.querySelector('[data-overlay-nav-toggle]');
      this.closeButton = this.querySelector('[data-overlay-nav-close]');
      this.cartCount = this.querySelector('[data-overlay-nav-cart-count]');
      /* The mobile cart FAB's badge (snippets/header-overlay-nav.liquid, "THE
         MOBILE CART FAB") is a SEPARATE attribute from cartCount above, not
         another match for the same one: cartCount renders as parenthesised
         text inside a link's label ("Cart (2)"), which is the wrong shape
         for a bare numeral sitting on an icon badge — see syncCartCount. */
      this.cartBadges = Array.prototype.slice.call(this.querySelectorAll('[data-overlay-nav-cart-badge]'));
      this.links = Array.prototype.slice.call(this.querySelectorAll('[data-overlay-nav-link]'));

      /* No panel or no toggle means the markup did not render — nothing to bind,
         and binding half of it would leave a control that opens nothing. */
      if (!this.panel || !this.toggleButton) return;

      this.isOpen = this.panel.classList.contains(OPEN_CLASS);
      /* Adopt whatever state the markup arrived in, so the host modifier can
         never start out of step with the panel it mirrors. */
      this.classList.toggle(HOST_OPEN_CLASS, this.isOpen);
      document.documentElement.classList.toggle(ROOT_OPEN_CLASS, this.isOpen);
      this.shouldLockScroll = readBool(this, 'data-lock-scroll', true);
      this.closeOnLinkClick = readBool(this, 'data-close-on-link', true);

      this.onToggleClick = this.onToggleClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onPanelClick = this.onPanelClick.bind(this);
      this.onLinkClick = this.onLinkClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onEditorEvent = this.onEditorEvent.bind(this);
      this.syncCartCount = this.syncCartCount.bind(this);

      this.toggleButton.addEventListener('click', this.onToggleClick);
      if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseClick);
      this.panel.addEventListener('click', this.onPanelClick);
      this.links.forEach(
        function (link) {
          link.addEventListener('click', this.onLinkClick);
        }.bind(this)
      );

      this.bindCartMirror();
      this.bindEditor();

      this.bound = true;
    }

    disconnectedCallback() {
      if (!this.bound) return;

      /* Release the lock before anything else. A component that is torn down
         while open would otherwise leave its owner token in the ref-count
         forever, and the page could never scroll again. */
      this.unlock();
      document.removeEventListener('keydown', this.onKeyDown);

      this.toggleButton.removeEventListener('click', this.onToggleClick);
      if (this.closeButton) this.closeButton.removeEventListener('click', this.onCloseClick);
      this.panel.removeEventListener('click', this.onPanelClick);
      this.links.forEach(
        function (link) {
          link.removeEventListener('click', this.onLinkClick);
        }.bind(this)
      );

      if (this.cartObserver) {
        this.cartObserver.disconnect();
        this.cartObserver = null;
      }

      if (this.editorEvents) {
        this.editorEvents.forEach(
          function (name) {
            document.removeEventListener(name, this.onEditorEvent);
          }.bind(this)
        );
        this.editorEvents = null;
      }

      /* Only release the focus trap if it was OURS. `removeTrapFocus` is global
         and shared with the cart drawer and every modal, so calling it on a
         teardown of a CLOSED overlay would quietly un-trap whatever else is open
         — a theme-editor re-render while the cart drawer is up is exactly that
         case. */
      if (this.isOpen && typeof removeTrapFocus === 'function') removeTrapFocus();

      this.bound = false;
    }

    /* ---------------------------------------------------------------- state */

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.panel.classList.add(OPEN_CLASS);
      this.classList.add(HOST_OPEN_CLASS);
      document.documentElement.classList.add(ROOT_OPEN_CLASS);
      this.toggleButton.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onKeyDown);

      this.lock();

      /* Focus is NOT moved in the theme editor. The merchant is typing in a
         settings field in the parent frame; pulling focus into the preview
         iframe interrupts the edit that opened the overlay in the first place. */
      if (isDesignMode()) return;

      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, this.links[0] || this.closeButton || this.panel);
      } else if (this.links[0]) {
        this.links[0].focus();
      }
    }

    close(options) {
      if (!this.isOpen) return;
      var opts = options || {};

      this.isOpen = false;
      this.panel.classList.remove(OPEN_CLASS);
      this.classList.remove(HOST_OPEN_CLASS);
      document.documentElement.classList.remove(ROOT_OPEN_CLASS);
      this.toggleButton.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeyDown);
      this.unlock();

      if (typeof removeTrapFocus === 'function') {
        /* Focus goes back to the control that opened the dialog — except when a
           link was clicked, because that click is a navigation and moving focus
           first would fight the browser's own focus handling on the new
           document (or on the anchor target of a same-page jump). */
        removeTrapFocus(opts.restoreFocus === false || isDesignMode() ? null : this.toggleButton);
      }
    }

    lock() {
      if (!this.shouldLockScroll || isDesignMode()) return;
      if (window.KitInteractions && typeof window.KitInteractions.lockScroll === 'function') {
        window.KitInteractions.lockScroll(this);
        this.locked = true;
      }
    }

    unlock() {
      if (!this.locked) return;
      if (window.KitInteractions && typeof window.KitInteractions.unlockScroll === 'function') {
        window.KitInteractions.unlockScroll(this);
      }
      this.locked = false;
    }

    /* ------------------------------------------------------------- handlers */

    onToggleClick() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    onCloseClick() {
      this.close();
    }

    /* Backdrop dismiss. The test is `target === panel`, so a click anywhere in
       the content column (`.overlay-nav__body`, which fills the panel's inline
       axis) is not a dismiss — only the padding gutters above and below it are. */
    onPanelClick(event) {
      if (event.target === this.panel) this.close();
    }

    /* Required rather than a nicety: an in-page anchor (`/#shop`) fires no
       navigation, so without this the overlay stays over the section it just
       scrolled to. Never preventDefault — the link must still work. */
    onLinkClick() {
      if (!this.closeOnLinkClick) return;
      this.close({ restoreFocus: false });
    }

    onKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation();
        this.close();
      }
    }

    /* ---------------------------------------------------------- cart mirror */

    /* The overlay's cart link carries a live count, but it cannot BE
       `#cart-icon-bubble`: Dawn's cart AJAX replaces that element's innerHTML
       with the rendered `cart-icon-bubble` section, which would delete the
       label. So the bubble stays the source of truth and this mirrors it —
       one MutationObserver, no dependency on pubsub, and it works for every path
       that updates the cart (cart page, drawer, quick-add, quick-order list). */
    bindCartMirror() {
      if ((!this.cartCount && !this.cartBadges.length) || typeof window.MutationObserver !== 'function') return;

      var bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;

      this.cartObserver = new window.MutationObserver(this.syncCartCount);
      this.cartObserver.observe(bubble, { childList: true, subtree: true, characterData: true });
    }

    syncCartCount() {
      if (!this.cartCount && !this.cartBadges.length) return;

      var bubble = document.getElementById('cart-icon-bubble');
      var count = bubble ? bubble.querySelector('.cart-count-bubble') : null;

      /* Dawn omits the visible number above 99 and renders only the
         screen-reader text, so an empty read here means "a lot", not "none".
         Reporting it as none is the one wrong answer. */
      var visible = count ? count.querySelector('[aria-hidden="true"]') : null;
      var value = visible ? visible.textContent.trim() : '';
      var displayValue = value === '' ? '99+' : value;

      if (this.cartCount) {
        this.cartCount.textContent = count ? '(' + displayValue + ')' : '';
      }

      /* Bare numeral, not the parenthesised text above — this sits on an
         icon badge, not inside a link's label — and is-empty toggles the
         badge's own visibility (component-overlay-nav.css) the same way the
         server-rendered initial state does from cart.item_count. */
      this.cartBadges.forEach(function (badge) {
        badge.textContent = count ? displayValue : '';
        badge.classList.toggle('is-empty', !count);
      });
    }

    /* -------------------------------------------------------- theme editor */

    /* The overlay is a full-viewport fixed layer. Left to itself it covers the
       section a merchant is editing, so the rules are:
         · never open on load;
         · open when THIS section or one of ITS blocks is selected, because a
           nav link inside a closed overlay cannot be edited by someone who
           cannot see it (the same reasoning as the tab controller's
           shopify:block:select handling in kit-interactions.js);
         · close for anything else, including a deselect. */
    bindEditor() {
      if (!isDesignMode()) return;

      this.editorEvents = [
        'shopify:section:select',
        'shopify:section:deselect',
        'shopify:block:select',
        'shopify:block:deselect',
      ];

      this.editorEvents.forEach(
        function (name) {
          document.addEventListener(name, this.onEditorEvent);
        }.bind(this)
      );
    }

    onEditorEvent(event) {
      var target = event.target;
      var mine = Boolean(
        target &&
          (target === this ||
            (typeof target.contains === 'function' && (target.contains(this) || this.contains(target))))
      );

      if (!mine || event.type.indexOf('deselect') !== -1) {
        this.close({ restoreFocus: false });
        return;
      }

      this.open();
    }
  };

  if (!window.customElements.get('overlay-nav')) {
    window.customElements.define('overlay-nav', OverlayNav);
  }
})();
